<!-- src/pages/ProgramsPage.vue -->
<template>
    <main class="bridge-page" aria-label="The Bridge event page">
        <!-- =====================================================
             HERO
        ====================================================== -->
        <section class="bridge-hero" aria-labelledby="bridge-title">
            <div class="bridge-hero__glow bridge-hero__glow--one" aria-hidden="true"></div>

            <div class="bridge-hero__glow bridge-hero__glow--two" aria-hidden="true"></div>

            <div class="shell bridge-hero__shell">
                <div class="bridge-hero__copy">
                    <p class="eyebrow eyebrow--hero">
                        M.C Bridge of Hope
                    </p>



                    <h1 id="bridge-title" class="bridge-hero__title">
                        The Bridge
                    </h1>

                    <p class="bridge-hero__tagline">
                        Where ambition meets opportunity.
                    </p>

                    <div class="bridge-hero__topics">
                        <span>Entrepreneurship</span>
                        <i></i>
                        <span>Mentorship</span>
                        <i></i>
                        <span>Networking</span>
                    </div>

                    <p class="bridge-hero__description">
                        A South Sudanese and Sudanese community-led evening
                        connecting young adults with entrepreneurs,
                        business owners and professionals.
                    </p>
                    <br>
                    <div class="bridge-hero__partners">
                        <span>In partnership with</span>

                        <strong>
                            United Cultures SSD
                        </strong>

                        <i></i> &

                        <strong>
                            <a style="text-decoration: underline; color:inherit" href="https://www.afrofy.com.au/"
                                target="_blank" rel="noopener noreferrer">
                                AFROFY</a>
                        </strong>
                    </div>
                    <p class="bridge-hero__statement">
                        Learn. Connect. Grow.
                    </p>

                    <div class="bridge-hero__actions">
                        <a class="btn btn--primary" :href="registrationUrl" target="_blank" rel="noopener noreferrer">
                            Register now

                            <span aria-hidden="true">
                                →
                            </span>
                        </a>

                        <a class="btn btn--light" href="#event-details">
                            Event details
                        </a>
                    </div>

                    <p class="bridge-hero__notice">
                        Limited spaces available. Registration is required.
                    </p>
                </div>

                <!-- HERO ARTWORK -->
                <button class="bridge-hero__visual image-open-button" type="button"
                    aria-label="View The Bridge banner larger" @click="
                        openImage(
                            bannerImage,
                            'The Bridge — Where Ambition Meets Opportunity',
                            'landscape'
                        )
                        ">
                    <img :src="bannerImage"
                        alt="The Bridge event banner by M.C Bridge of Hope in partnership with United Cultures SSD and Afrofy" />

                    <span class="image-open-hint" aria-hidden="true">
                        <span v-html="ICONS.expand"></span>
                        View larger
                    </span>
                </button>
            </div>
        </section>


        <!-- =====================================================
             EVENT DETAILS
        ====================================================== -->
        <section id="event-details" class="event-details" aria-labelledby="event-details-title">
            <div class="shell">
                <div class="section-head">
                    <div class="section-head__copy">
                        <p class="eyebrow">
                            Event details
                        </p>

                        <h2 id="event-details-title">
                            Build the connections that move you forward.
                        </h2>

                        <p>
                            An evening designed to connect ambition with
                            knowledge, mentorship and opportunity.
                        </p>
                    </div>

                    <div class="event-head-meta">
                        <!-- DATE -->
                        <div class="event-date-pill">
                            <span class="event-date-pill__day">
                                18
                            </span>

                            <div>
                                <strong>
                                    September
                                </strong>

                                <small>
                                    2026
                                </small>
                            </div>
                        </div>

                        <!-- LOCATION -->
                        <a class="event-location-pill" :href="googleMapsUrl" target="_blank" rel="noopener noreferrer"
                            aria-label="Open Rouse Hill Community Centre in Google Maps">
                            <span class="event-location-pill__icon" aria-hidden="true" v-html="ICONS.location"></span>

                            <span class="event-location-pill__copy">
                                <small>
                                    Location
                                </small>

                                <strong>
                                    Rouse Hill Community Centre
                                </strong>

                                <em>
                                    Google Maps ↗
                                </em>
                            </span>
                        </a>
                    </div>
                </div>


                <!-- INFO CARDS -->
                <div class="event-info-grid">
                    <article v-for="item in eventInfo" :key="item.label" class="event-info-card">
                        <div class="event-info-card__icon" aria-hidden="true" v-html="item.icon"></div>

                        <div class="event-info-card__copy">
                            <span>
                                {{ item.label }}
                            </span>

                            <strong>
                                {{ item.value }}
                            </strong>

                            <small v-if="item.sub">
                                {{ item.sub }}
                            </small>
                        </div>
                    </article>
                </div>


                <!-- MAP -->
                <div class="event-map">
                    <div class="event-map__copy">
                        <span class="event-map__icon" aria-hidden="true" v-html="ICONS.location"></span>

                        <div>
                            <p class="eyebrow">
                                Event location
                            </p>

                            <h3>
                                Rouse Hill Community Centre
                            </h3>

                            <p>
                                Rouse Hill, New South Wales
                            </p>

                            <a class="map-link" :href="googleMapsUrl" target="_blank" rel="noopener noreferrer">
                                Open in Google Maps
                                <span>↗</span>
                            </a>
                        </div>
                    </div>

                    <div class="event-map__frame">
                        <iframe :src="googleMapsEmbedUrl" title="Google Map showing Rouse Hill Community Centre"
                            loading="lazy" allowfullscreen referrerpolicy="no-referrer-when-downgrade"></iframe>
                    </div>
                </div>
            </div>
        </section>


        <!-- =====================================================
             ABOUT
        ====================================================== -->
        <section class="bridge-about">
            <div class="shell bridge-about__grid">
                <div class="bridge-about__copy">
                    <p class="eyebrow">
                        About The Bridge
                    </p>

                    <h2>
                        Ambition is already there.

                        <span>
                            Sometimes, what’s needed is the bridge.
                        </span>
                    </h2>

                    <p>
                        The Bridge brings together young adults,
                        entrepreneurs, business owners and professionals
                        for an evening centred around real stories,
                        practical insight and meaningful connection.
                    </p>

                    <p>
                        Through panel conversations, live questions and
                        networking, attendees can learn from people with
                        lived experience and begin building relationships
                        that can support their next step.
                    </p>

                    <div class="bridge-about__quote">
                        <span aria-hidden="true">
                            “
                        </span>

                        <p>
                            Because ambition is already there.
                            Sometimes, what’s needed is the bridge.
                        </p>

                        <span aria-hidden="true">
                            ”
                        </span>
                    </div>
                </div>

                <!-- OFFICIAL FLYER -->
                <div class="flyer-frame flyer-frame--portrait">
                    <button class="image-open-button flyer-button" type="button"
                        aria-label="View official event flyer larger" @click="
                            openImage(
                                officialFlyer,
                                'Official flyer for The Bridge event',
                                'portrait'
                            )
                            ">
                        <img :src="officialFlyer" alt="Official flyer for The Bridge event" loading="lazy" />

                        <span class="image-open-hint" aria-hidden="true">
                            <span v-html="ICONS.expand"></span>
                            View larger
                        </span>
                    </button>
                </div>
            </div>
        </section>


        <!-- =====================================================
             WHAT TO EXPECT
        ====================================================== -->
        <section class="experience-section">
            <div class="shell">
                <div class="section-head section-head--simple">
                    <div class="section-head__copy">
                        <p class="eyebrow">
                            What to expect
                        </p>

                        <h2>
                            Real conversations.
                            Practical connections.
                        </h2>

                        <p>
                            The evening is structured around three
                            core experiences.
                        </p>
                    </div>
                </div>

                <div class="experience-grid">
                    <article v-for="item in experiences" :key="item.title" class="experience-card">
                        <div class="experience-card__top">
                            <div class="experience-card__icon" v-html="item.icon" aria-hidden="true"></div>

                            <span class="experience-card__number">
                                {{ item.number }}
                            </span>
                        </div>

                        <h3>
                            {{ item.title }}
                        </h3>

                        <p>
                            {{ item.text }}
                        </p>
                    </article>
                </div>
            </div>
        </section>


        <!-- =====================================================
             WHO IT IS FOR
        ====================================================== -->
        <section class="audience-section">
            <div class="shell audience-section__grid">
                <!-- SECOND FLYER -->
                <div class="flyer-frame flyer-frame--portrait">
                    <button class="image-open-button flyer-button" type="button"
                        aria-label="View The Bridge community event flyer larger" @click="
                            openImage(
                                secondFlyer,
                                'The Bridge community event flyer',
                                'portrait'
                            )
                            ">
                        <img :src="secondFlyer" alt="The Bridge community event flyer" loading="lazy" />

                        <span class="image-open-hint" aria-hidden="true">
                            <span v-html="ICONS.expand"></span>
                            View larger
                        </span>
                    </button>
                </div>

                <div class="audience-section__copy">
                    <p class="eyebrow">
                        Who it’s for
                    </p>

                    <h2>
                        A community-led space for learning and growth.
                    </h2>

                    <p>
                        The Bridge is designed for young people looking
                        to learn, meet others and gain practical insight
                        from entrepreneurs, business owners and
                        professionals.
                    </p>

                    <div class="audience-list">
                        <div v-for="item in audiences" :key="item" class="audience-list__item">
                            <span aria-hidden="true">
                                ✓
                            </span>

                            <strong>
                                {{ item }}
                            </strong>
                        </div>
                    </div>

                    <a class="text-link" :href="registrationUrl" target="_blank" rel="noopener noreferrer">
                        Register for The Bridge
                        <span>→</span>
                    </a>
                </div>
            </div>
        </section>


        <!-- =====================================================
             PARTNERS
        ====================================================== -->
        <section class="partners-section">
            <div class="shell">
                <div class="section-head section-head--center">
                    <div class="section-head__copy">
                        <p class="eyebrow">
                            Working together
                        </p>

                        <h2>
                            In partnership with community.
                        </h2>

                        <p>
                            The Bridge is made possible through
                            organisations working together to create
                            stronger connections and opportunity.
                        </p>
                    </div>
                </div>

                <div class="partners-grid">
                    <article v-for="partner in partners" :key="partner.name" class="partner-card">
                        <span class="partner-card__type">
                            {{ partner.type }}
                        </span>

                        <h3>
                            {{ partner.name }}
                        </h3>

                        <p>
                            {{ partner.description }}
                        </p>

                        <a v-if="partner.url" :href="partner.url" target="_blank" rel="noopener noreferrer">
                            Visit website
                            <span>↗</span>
                        </a>
                    </article>
                </div>
            </div>
        </section>


        <!-- =====================================================
             REGISTER
        ====================================================== -->
        <section class="register-section">
            <div class="shell">
                <div class="register-panel">
                    <div class="register-panel__copy">
                        <p class="eyebrow eyebrow--hero">
                            Friday 18 September 2026
                        </p>

                        <h2>
                            Ready to cross The Bridge?
                        </h2>

                        <p>
                            Join us for an evening of entrepreneurship,
                            mentorship, networking and real conversations.
                        </p>

                        <div class="register-panel__details">
                            <span>
                                6:00 PM – 8:45 PM
                            </span>

                            <i></i>

                            <span>
                                Doors open 5:30 PM
                            </span>

                            <i></i>

                            <a :href="googleMapsUrl" target="_blank" rel="noopener noreferrer">
                                Rouse Hill Community Centre ↗
                            </a>
                        </div>
                    </div>

                    <div class="register-panel__action">
                        <a class="btn btn--gold" :href="registrationUrl" target="_blank" rel="noopener noreferrer">
                            Register now

                            <span aria-hidden="true">
                                →
                            </span>
                        </a>

                        <small>
                            Limited spaces available
                        </small>
                    </div>
                </div>
            </div>
        </section>


        <!-- =====================================================
             LAUNCH STRIP
        ====================================================== -->
        <section class="launch-strip">
            <div class="shell launch-strip__inner">
                <span class="launch-strip__mark" aria-hidden="true">
                    ✦
                </span>

                <p>
                    Celebrating the official launch of
                    <strong>
                        M.C Bridge of Hope
                    </strong>
                </p>

                <div class="launch-strip__values">
                    <span>People</span>
                    <i></i>
                    <span>Knowledge</span>
                    <i></i>
                    <span>Opportunity</span>
                </div>
            </div>
        </section>


        <!-- =====================================================
             IMAGE VIEWER
        ====================================================== -->
        <Teleport to="body">
            <transition name="lightbox">
                <div v-if="lightboxImage" class="image-lightbox" role="dialog" aria-modal="true"
                    :aria-label="lightboxAlt || 'Image preview'" @click.self="closeImage">
                    <div class="image-lightbox__topbar">
                        <div class="image-lightbox__title">
                            <span class="image-lightbox__title-icon" aria-hidden="true" v-html="ICONS.image"></span>

                            <span>
                                {{ lightboxAlt }}
                            </span>
                        </div>

                        <button class="image-lightbox__close" type="button" aria-label="Close image preview"
                            @click="closeImage">
                            <span aria-hidden="true">
                                ×
                            </span>
                        </button>
                    </div>

                    <div class="image-lightbox__viewport" @click.self="closeImage">
                        <figure class="image-lightbox__figure" :class="{
                            'image-lightbox__figure--portrait':
                                lightboxOrientation === 'portrait',
                            'image-lightbox__figure--landscape':
                                lightboxOrientation === 'landscape'
                        }">
                            <img class="image-lightbox__image" :src="lightboxImage" :alt="lightboxAlt" />

                            <figcaption v-if="lightboxAlt" class="image-lightbox__caption">
                                {{ lightboxAlt }}
                            </figcaption>
                        </figure>
                    </div>

                    <p class="image-lightbox__help">
                        Click outside the image or press Esc to close
                    </p>
                </div>
            </transition>
        </Teleport>
    </main>
</template>


<script>
import { useHead } from "@unhead/vue";

import bannerImage from "@/assets/Banner.png";
import officialFlyer from "@/assets/Offical Flyer.png";
import secondFlyer from "@/assets/Flyer#2.png";


const ICONS = {
    calendar: `
        <svg
            viewBox="0 0 24 24"
            width="25"
            height="25"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
        >
            <rect
                x="3"
                y="5"
                width="18"
                height="16"
                rx="3"
                stroke="currentColor"
                stroke-width="1.8"
            />

            <path
                d="M7 3v4M17 3v4M3 10h18"
                stroke="currentColor"
                stroke-width="1.8"
                stroke-linecap="round"
            />
        </svg>
    `,

    clock: `
        <svg
            viewBox="0 0 24 24"
            width="25"
            height="25"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
        >
            <circle
                cx="12"
                cy="12"
                r="9"
                stroke="currentColor"
                stroke-width="1.8"
            />

            <path
                d="M12 7v5l3.5 2"
                stroke="currentColor"
                stroke-width="1.8"
                stroke-linecap="round"
                stroke-linejoin="round"
            />
        </svg>
    `,

    location: `
        <svg
            viewBox="0 0 24 24"
            width="25"
            height="25"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
        >
            <path
                d="M12 21s7-6.1 7-12A7 7 0 1 0 5 9c0 5.9 7 12 7 12Z"
                stroke="currentColor"
                stroke-width="1.8"
                stroke-linejoin="round"
            />

            <circle
                cx="12"
                cy="9"
                r="2.3"
                stroke="currentColor"
                stroke-width="1.8"
            />
        </svg>
    `,

    people: `
        <svg
            viewBox="0 0 24 24"
            width="29"
            height="29"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
        >
            <circle
                cx="9"
                cy="8"
                r="3"
                stroke="currentColor"
                stroke-width="1.7"
            />

            <circle
                cx="17"
                cy="9"
                r="2.4"
                stroke="currentColor"
                stroke-width="1.7"
            />

            <path
                d="M3.5 19c.4-3.4 2.3-5.3 5.5-5.3s5.1 1.9 5.5 5.3"
                stroke="currentColor"
                stroke-width="1.7"
                stroke-linecap="round"
            />

            <path
                d="M15.2 14.1c3.3-.5 5.1 1.1 5.3 4.2"
                stroke="currentColor"
                stroke-width="1.7"
                stroke-linecap="round"
            />
        </svg>
    `,

    chat: `
        <svg
            viewBox="0 0 24 24"
            width="29"
            height="29"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
        >
            <path
                d="M4 5.5h16v11H9l-5 3v-14Z"
                stroke="currentColor"
                stroke-width="1.8"
                stroke-linejoin="round"
            />

            <path
                d="M8 10h8M8 13h5"
                stroke="currentColor"
                stroke-width="1.8"
                stroke-linecap="round"
            />
        </svg>
    `,

    handshake: `
        <svg
            viewBox="0 0 24 24"
            width="29"
            height="29"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
        >
            <path
                d="m3 10 4-4 4 2 2-1 4 2 4 4-5 5-4-3-3 2-6-7Z"
                stroke="currentColor"
                stroke-width="1.7"
                stroke-linejoin="round"
            />

            <path
                d="m8 13 4 3M11 11l5 4"
                stroke="currentColor"
                stroke-width="1.7"
                stroke-linecap="round"
            />
        </svg>
    `,

    expand: `
        <svg
            viewBox="0 0 24 24"
            width="16"
            height="16"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
        >
            <path
                d="M8 3H3v5M16 3h5v5M8 21H3v-5M16 21h5v-5"
                stroke="currentColor"
                stroke-width="1.8"
                stroke-linecap="round"
                stroke-linejoin="round"
            />
        </svg>
    `,

    image: `
        <svg
            viewBox="0 0 24 24"
            width="18"
            height="18"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
        >
            <rect
                x="3"
                y="4"
                width="18"
                height="16"
                rx="3"
                stroke="currentColor"
                stroke-width="1.7"
            />

            <circle
                cx="9"
                cy="9"
                r="1.5"
                stroke="currentColor"
                stroke-width="1.7"
            />

            <path
                d="m5.5 17 4.5-4 3 2.5 2.5-2 3 3.5"
                stroke="currentColor"
                stroke-width="1.7"
                stroke-linecap="round"
                stroke-linejoin="round"
            />
        </svg>
    `,
};


export default {
    name: "ProgramsPage",


    setup() {
        useHead({
            title:
                "The Bridge | Entrepreneurship, Mentorship & Networking | M.C Bridge of Hope",

            meta: [
                {
                    name: "description",
                    content:
                        "Join The Bridge on 18 September 2026 at Rouse Hill Community Centre. A South Sudanese and Sudanese community-led evening of entrepreneurship, mentorship, networking, panel discussion and live Q&A.",
                },

                {
                    name: "keywords",
                    content:
                        "The Bridge event, M.C Bridge of Hope, South Sudanese community Sydney, Sudanese community Sydney, entrepreneurship event, mentorship Sydney, networking event, Rouse Hill, Afrofy, United Cultures SSD",
                },

                {
                    name: "author",
                    content: "M.C Bridge of Hope",
                },

                {
                    name: "robots",
                    content:
                        "index, follow, max-image-preview:large, max-snippet:-1, max-video-preview:-1",
                },

                {
                    property: "og:title",
                    content:
                        "The Bridge | Where Ambition Meets Opportunity",
                },

                {
                    property: "og:description",
                    content:
                        "Entrepreneurship, mentorship and networking for young adults. Friday 18 September 2026 at Rouse Hill Community Centre.",
                },

                {
                    property: "og:type",
                    content: "website",
                },

                {
                    property: "og:url",
                    content:
                        "https://marychan.org.au/programs",
                },

                {
                    property: "og:image",
                    content:
                        "https://marychan.org.au/Banner.png",
                },

                {
                    property: "og:image:alt",
                    content:
                        "The Bridge - Where Ambition Meets Opportunity",
                },

                {
                    property: "og:site_name",
                    content:
                        "M.C Bridge of Hope",
                },

                {
                    property: "og:locale",
                    content:
                        "en_AU",
                },

                {
                    name: "twitter:card",
                    content:
                        "summary_large_image",
                },

                {
                    name: "twitter:title",
                    content:
                        "The Bridge | Where Ambition Meets Opportunity",
                },

                {
                    name: "twitter:description",
                    content:
                        "Join The Bridge on 18 September 2026 for entrepreneurship, mentorship, networking, panel discussion and live Q&A.",
                },

                {
                    name: "twitter:image",
                    content:
                        "https://marychan.org.au/Banner.png",
                },
            ],

            link: [
                {
                    rel: "canonical",
                    href:
                        "https://marychan.org.au/programs",
                },

                {
                    rel: "alternate",
                    hrefLang: "en-au",
                    href:
                        "https://marychan.org.au/programs",
                },

                {
                    rel: "alternate",
                    hrefLang: "x-default",
                    href:
                        "https://marychan.org.au/programs",
                },
            ],

            script: [
                {
                    type: "application/ld+json",

                    children:
                        JSON.stringify({
                            "@context":
                                "https://schema.org",

                            "@type":
                                "Event",

                            name:
                                "The Bridge - Where Ambition Meets Opportunity",

                            description:
                                "A South Sudanese and Sudanese community-led evening connecting young adults with entrepreneurs, business owners and professionals through entrepreneurship, mentorship and networking.",

                            startDate:
                                "2026-09-18T18:00:00+10:00",

                            endDate:
                                "2026-09-18T20:45:00+10:00",

                            doorTime:
                                "2026-09-18T17:30:00+10:00",

                            eventAttendanceMode:
                                "https://schema.org/OfflineEventAttendanceMode",

                            eventStatus:
                                "https://schema.org/EventScheduled",

                            image: [
                                "https://marychan.org.au/Banner.png",
                            ],

                            location: {
                                "@type":
                                    "Place",

                                name:
                                    "Rouse Hill Community Centre",

                                address: {
                                    "@type":
                                        "PostalAddress",

                                    addressLocality:
                                        "Rouse Hill",

                                    addressRegion:
                                        "NSW",

                                    addressCountry:
                                        "AU",
                                },
                            },

                            offers: {
                                "@type":
                                    "Offer",

                                url:
                                    "https://forms.gle/Q7EMmSVPMBz1xCiZ7",

                                availability:
                                    "https://schema.org/InStock",

                                price:
                                    "0",

                                priceCurrency:
                                    "AUD",
                            },

                            organizer: {
                                "@type":
                                    "Organization",

                                name:
                                    "M.C Bridge of Hope",

                                url:
                                    "https://marychan.org.au/",
                            },
                        }),
                },
            ],
        });

        return {};
    },


    data() {
        return {
            ICONS,

            bannerImage,
            officialFlyer,
            secondFlyer,

            lightboxImage:
                null,

            lightboxAlt:
                "",

            lightboxOrientation:
                "landscape",

            googleMapsUrl:
                "https://www.google.com/maps/search/?api=1&query=Rouse+Hill+Community+Centre",

            googleMapsEmbedUrl:
                "https://www.google.com/maps?q=Rouse+Hill+Community+Centre&output=embed",

            registrationUrl:
                "https://forms.gle/Q7EMmSVPMBz1xCiZ7",

            eventInfo: [
                {
                    label:
                        "Date",

                    value:
                        "Friday, 18 September 2026",

                    sub:
                        "Registration required",

                    icon:
                        ICONS.calendar,
                },

                {
                    label:
                        "Time",

                    value:
                        "6:00 PM – 8:45 PM",

                    sub:
                        "Doors open 5:30 PM",

                    icon:
                        ICONS.clock,
                },

                {
                    label:
                        "Location",

                    value:
                        "Rouse Hill Community Centre",

                    sub:
                        "Rouse Hill, NSW",

                    icon:
                        ICONS.location,
                },
            ],

            experiences: [
                {
                    number:
                        "01",

                    title:
                        "Panel Discussion",

                    text:
                        "Real stories and practical insights from entrepreneurs, business owners and professionals.",

                    icon:
                        ICONS.people,
                },

                {
                    number:
                        "02",

                    title:
                        "Live Q&A",

                    text:
                        "Ask questions, hear practical answers and learn directly from people with experience.",

                    icon:
                        ICONS.chat,
                },

                {
                    number:
                        "03",

                    title:
                        "Networking",

                    text:
                        "Meet, connect and build relationships with ambitious people across the community.",

                    icon:
                        ICONS.handshake,
                },
            ],

            audiences: [
                "Young adults exploring entrepreneurship",
                "Aspiring entrepreneurs and founders",
                "Business owners and professionals",
                "People looking for mentorship",
                "Anyone wanting to build meaningful professional connections",
            ],

            partners: [
                {
                    type:
                        "Presented by",

                    name:
                        "M.C Bridge of Hope",

                    description:
                        "Connecting people, knowledge and opportunity through community-led initiatives.",

                    url:
                        "https://marychan.org.au/",
                },

                {
                    type:
                        "In partnership with",

                    name:
                        "United Cultures SSD",

                    description:
                        "Supporting community connection and opportunity for South Sudanese and Sudanese people.",

                    url:
                        "",
                },

                {
                    type:
                        "In partnership with",

                    name:
                        "Afrofy",

                    description:
                        "Connecting African and diaspora culture, businesses, services and communities across Australia.",

                    url:
                        "https://www.afrofy.com.au/",
                },
            ],
        };
    },


    mounted() {
        if (
            typeof window !==
            "undefined"
        ) {
            window.scrollTo(
                0,
                0,
            );

            window.addEventListener(
                "keydown",
                this.onKeydown,
            );
        }
    },


    beforeUnmount() {
        if (
            typeof window !==
            "undefined"
        ) {
            window.removeEventListener(
                "keydown",
                this.onKeydown,
            );
        }

        if (
            typeof document !==
            "undefined"
        ) {
            document.body.style.overflow =
                "";
        }
    },


    methods: {
        openImage(
            src,
            alt = "",
            orientation = "landscape",
        ) {
            this.lightboxImage =
                src;

            this.lightboxAlt =
                alt;

            this.lightboxOrientation =
                orientation;

            if (
                typeof document !==
                "undefined"
            ) {
                document.body.style.overflow =
                    "hidden";
            }
        },


        closeImage() {
            this.lightboxImage =
                null;

            this.lightboxAlt =
                "";

            this.lightboxOrientation =
                "landscape";

            if (
                typeof document !==
                "undefined"
            ) {
                document.body.style.overflow =
                    "";
            }
        },


        onKeydown(event) {
            if (
                event.key ===
                "Escape" &&
                this.lightboxImage
            ) {
                this.closeImage();
            }
        },
    },
};
</script>


<style scoped lang="scss">
.bridge-page {
    --navy-950: #02182b;
    --navy-900: #03233e;
    --navy-800: #063659;
    --navy-700: #0b4a70;

    --gold-600: #b87906;
    --gold-500: #d99a16;
    --gold-400: #edb335;
    --gold-300: #f4c862;

    --cream: #f8f5ee;
    --cream-soft: #fcfaf6;
    --paper: #ffffff;

    --ink: #0b223d;
    --muted: #647183;

    --line: rgba(7, 42, 73, 0.1);

    --radius-md: 18px;
    --radius-lg: 27px;
    --radius-xl: 38px;

    --shadow-sm:
        0 12px 34px rgba(2, 24, 43, 0.08);

    --shadow-md:
        0 24px 60px rgba(2, 24, 43, 0.12);

    --shadow-lg:
        0 38px 90px rgba(2, 24, 43, 0.18);

    min-height: 100%;
    overflow: hidden;
    color: var(--ink);
    background: var(--paper);

    * {
        box-sizing: border-box;
    }


    /* =====================================================
       SHARED
    ====================================================== */

    .shell {
        width:
            min(1180px,
                calc(100% - 32px));

        margin:
            0 auto;

        @media (min-width: 760px) {
            width:
                min(1180px,
                    calc(100% - 52px));
        }

        @media (min-width: 1180px) {
            width:
                min(1240px,
                    calc(100% - 72px));
        }
    }


    .eyebrow {
        margin:
            0 0 11px;

        color:
            var(--gold-600);

        font-size:
            11px;

        line-height:
            1.2;

        font-weight:
            900;

        letter-spacing:
            0.17em;

        text-transform:
            uppercase;

        &.eyebrow--hero {
            color:
                var(--gold-300);
        }
    }


    .btn {
        appearance: none;

        display:
            inline-flex;

        align-items:
            center;

        justify-content:
            center;

        gap:
            10px;

        min-height:
            51px;

        padding:
            13px 22px;

        border:
            1px solid transparent;

        border-radius:
            999px;

        cursor:
            pointer;

        font-family:
            inherit;

        font-size:
            14px;

        font-weight:
            900;

        line-height:
            1;

        text-decoration:
            none;

        transition:
            transform 180ms ease,
            box-shadow 180ms ease,
            background 180ms ease,
            border-color 180ms ease;

        >span {
            font-size:
                17px;

            transition:
                transform 180ms ease;
        }

        &:hover {
            transform:
                translateY(-2px);

            >span {
                transform:
                    translateX(3px);
            }
        }

        &:focus-visible {
            outline:
                3px solid rgba(237,
                    179,
                    53,
                    0.42);

            outline-offset:
                3px;
        }

        &.btn--primary,
        &.btn--gold {
            color:
                var(--navy-950);

            background:
                linear-gradient(135deg,
                    #f9d173 0%,
                    var(--gold-400) 45%,
                    var(--gold-500) 100%);

            box-shadow:
                0 16px 36px rgba(217,
                    154,
                    22,
                    0.24);

            &:hover {
                box-shadow:
                    0 22px 45px rgba(217,
                        154,
                        22,
                        0.34);
            }
        }

        &.btn--light {
            color:
                #fff;

            border-color:
                rgba(255,
                    255,
                    255,
                    0.22);

            background:
                rgba(255,
                    255,
                    255,
                    0.08);

            backdrop-filter:
                blur(14px);

            &:hover {
                border-color:
                    rgba(255,
                        255,
                        255,
                        0.34);

                background:
                    rgba(255,
                        255,
                        255,
                        0.14);
            }
        }

        @media (max-width: 560px) {
            width:
                100%;
        }
    }


    .text-link {
        display:
            inline-flex;

        align-items:
            center;

        gap:
            7px;

        color:
            var(--gold-600);

        font-size:
            14px;

        font-weight:
            900;

        text-decoration:
            none;

        &:hover {
            text-decoration:
                underline;
        }
    }


    /* =====================================================
       HERO
    ====================================================== */

    .bridge-hero {
        position:
            relative;

        isolation:
            isolate;

        overflow:
            hidden;

        padding:
            80px 0 74px;

        color:
            #fff;

        background:
            radial-gradient(780px 520px at 82% -5%,
                rgba(237,
                    179,
                    53,
                    0.21),
                transparent 62%),
            radial-gradient(580px 440px at 0% 90%,
                rgba(11,
                    74,
                    112,
                    0.34),
                transparent 68%),
            linear-gradient(135deg,
                var(--navy-950),
                var(--navy-900),
                #073a5e);

        &::after {
            position:
                absolute;

            right: 0;
            bottom: 0;
            left: 0;

            height:
                1px;

            content:
                "";

            background:
                linear-gradient(90deg,
                    transparent,
                    rgba(237,
                        179,
                        53,
                        0.7),
                    transparent);
        }

        @media (max-width: 720px) {
            padding:
                54px 0 48px;
        }
    }


    .bridge-hero__glow {
        position:
            absolute;

        border-radius:
            50%;

        pointer-events:
            none;

        filter:
            blur(6px);
    }


    .bridge-hero__glow--one {
        top:
            -190px;

        left:
            -160px;

        width:
            430px;

        height:
            430px;

        background:
            rgba(237,
                179,
                53,
                0.09);
    }


    .bridge-hero__glow--two {
        right:
            -220px;

        bottom:
            -260px;

        width:
            540px;

        height:
            540px;

        background:
            rgba(255,
                255,
                255,
                0.04);
    }


    .bridge-hero__shell {
        position:
            relative;

        z-index:
            2;

        display:
            grid;

        gap:
            44px;

        align-items:
            center;

        @media (min-width: 980px) {
            grid-template-columns:
                minmax(0,
                    0.9fr) minmax(500px,
                    1.1fr);
        }
    }


    .bridge-hero__copy {
        max-width:
            660px;
    }


    .bridge-hero__partners {
        display:
            flex;

        flex-wrap:
            wrap;

        align-items:
            center;

        gap:
            9px;

        margin-bottom:
            24px;

        span {
            color:
                rgba(255,
                    255,
                    255,
                    0.51);

            font-size:
                10px;

            font-weight:
                900;

            letter-spacing:
                0.13em;

            text-transform:
                uppercase;
        }

        strong {
            color:
                rgba(255,
                    255,
                    255,
                    0.9);

            font-size:
                11px;

            font-weight:
                900;
        }

        i {
            width:
                4px;

            height:
                4px;

            border-radius:
                50%;

            background:
                var(--gold-400);
        }
    }


    .bridge-hero__title {
        margin: 0;

        color:
            #fff;

        font-family:
            Georgia,
            "Times New Roman",
            serif;

        font-size:
            clamp(66px,
                8.5vw,
                122px);

        line-height:
            0.83;

        font-weight:
            500;

        letter-spacing:
            -0.055em;

        text-transform:
            uppercase;

        @media (max-width: 560px) {
            font-size:
                clamp(58px,
                    21vw,
                    84px);
        }
    }


    .bridge-hero__tagline {
        max-width:
            600px;

        margin:
            26px 0 0;

        color:
            var(--gold-300);

        font-size:
            clamp(19px,
                2.25vw,
                30px);

        line-height:
            1.15;

        font-weight:
            900;

        letter-spacing:
            -0.02em;

        text-transform:
            uppercase;
    }


    .bridge-hero__topics {
        display:
            flex;

        flex-wrap:
            wrap;

        align-items:
            center;

        gap:
            12px;

        margin-top:
            21px;

        color:
            rgba(255,
                255,
                255,
                0.86);

        font-size:
            11px;

        font-weight:
            850;

        letter-spacing:
            0.075em;

        text-transform:
            uppercase;

        i {
            width:
                5px;

            height:
                5px;

            border-radius:
                50%;

            background:
                var(--gold-400);
        }
    }


    .bridge-hero__description {
        max-width:
            58ch;

        margin:
            25px 0 0;

        color:
            rgba(255,
                255,
                255,
                0.7);

        font-size:
            16px;

        line-height:
            1.8;
    }


    .bridge-hero__statement {
        margin:
            17px 0 0;

        color:
            var(--gold-300);

        font-size:
            18px;

        font-weight:
            900;
    }


    .bridge-hero__actions {
        display:
            flex;

        flex-wrap:
            wrap;

        gap:
            12px;

        margin-top:
            28px;

        @media (max-width: 560px) {
            display:
                grid;
        }
    }


    .bridge-hero__notice {
        margin:
            13px 0 0;

        color:
            rgba(255,
                255,
                255,
                0.46);

        font-size:
            11px;

        font-weight:
            700;
    }


    .bridge-hero__visual {
        position:
            relative;

        overflow:
            hidden;

        min-height:
            350px;

        border:
            1px solid rgba(255,
                255,
                255,
                0.16);

        border-radius:
            var(--radius-xl);

        background:
            rgba(255,
                255,
                255,
                0.06);

        box-shadow:
            0 38px 88px rgba(0,
                0,
                0,
                0.32);

        @media (min-width: 760px) {
            min-height:
                400px;
        }

        @media (min-width: 980px) {
            min-height:
                445px;
        }

        @media (max-width: 560px) {
            min-height:
                250px;
        }

        img {
            position:
                absolute;

            inset: 0;

            width:
                100%;

            height:
                100%;

            object-fit:
                cover;

            object-position:
                center;

            transition:
                transform 350ms ease;
        }

        &:hover {
            img {
                transform:
                    scale(1.018);
            }
        }
    }


    /* =====================================================
       IMAGE BUTTON
    ====================================================== */

    .image-open-button {
        appearance:
            none;

        position:
            relative;

        display:
            block;

        width:
            100%;

        padding: 0;

        border: 0;

        color:
            inherit;

        background:
            transparent;

        cursor:
            zoom-in;

        font:
            inherit;

        text-align:
            inherit;

        &:focus-visible {
            outline:
                3px solid var(--gold-400);

            outline-offset:
                4px;
        }

        &:hover {
            .image-open-hint {
                opacity: 1;

                transform:
                    translateY(0);
            }
        }
    }


    .image-open-hint {
        position:
            absolute;

        right:
            14px;

        bottom:
            14px;

        z-index:
            4;

        display:
            inline-flex;

        align-items:
            center;

        gap:
            7px;

        min-height:
            37px;

        padding:
            8px 12px;

        border:
            1px solid rgba(255,
                255,
                255,
                0.2);

        border-radius:
            999px;

        color:
            #fff;

        background:
            rgba(2,
                24,
                43,
                0.8);

        backdrop-filter:
            blur(13px);

        box-shadow:
            0 10px 28px rgba(0,
                0,
                0,
                0.2);

        font-size:
            10px;

        font-weight:
            850;

        opacity: 0;

        transform:
            translateY(6px);

        transition:
            opacity 180ms ease,
            transform 180ms ease;

        >span {
            display:
                grid;

            place-items:
                center;
        }

        @media (max-width: 720px) {
            opacity: 1;

            transform:
                none;
        }
    }


    /* =====================================================
       SECTION HEAD
    ====================================================== */

    .section-head {
        display:
            grid;

        gap:
            24px;

        align-items:
            end;

        margin-bottom:
            30px;

        @media (min-width: 840px) {
            grid-template-columns:
                minmax(0,
                    1fr) auto;
        }

        h2 {
            max-width:
                820px;

            margin: 0;

            color:
                var(--navy-900);

            font-size:
                clamp(34px,
                    4.5vw,
                    58px);

            line-height:
                1;

            font-weight:
                950;

            letter-spacing:
                -0.045em;
        }

        p:not(.eyebrow) {
            max-width:
                680px;

            margin:
                14px 0 0;

            color:
                var(--muted);

            line-height:
                1.75;
        }

        &.section-head--simple {
            display:
                block;
        }

        &.section-head--center {
            display:
                block;

            text-align:
                center;

            .section-head__copy {
                max-width:
                    760px;

                margin:
                    0 auto;
            }

            h2,
            p {
                margin-left:
                    auto;

                margin-right:
                    auto;
            }
        }
    }


    /* =====================================================
       EVENT DETAILS
    ====================================================== */

    .event-details {
        padding:
            78px 0;

        background:
            radial-gradient(620px 280px at 100% 0%,
                rgba(217,
                    154,
                    22,
                    0.08),
                transparent 70%),
            linear-gradient(180deg,
                #fff,
                var(--cream-soft));

        @media (max-width: 720px) {
            padding:
                54px 0;
        }
    }


    .event-head-meta {
        display:
            flex;

        flex-wrap:
            wrap;

        justify-content:
            flex-end;

        gap:
            10px;

        @media (max-width: 839px) {
            justify-content:
                flex-start;
        }

        @media (max-width: 560px) {
            display:
                grid;

            width:
                100%;
        }
    }


    .event-date-pill,
    .event-location-pill {
        min-height:
            78px;

        border:
            1px solid var(--line);

        border-radius:
            20px;

        background:
            rgba(255,
                255,
                255,
                0.94);

        box-shadow:
            var(--shadow-sm);
    }


    .event-date-pill {
        display:
            flex;

        align-items:
            center;

        gap:
            13px;

        padding:
            12px 17px;

        div {
            display:
                grid;

            gap:
                2px;
        }

        strong {
            color:
                var(--navy-900);

            font-size:
                14px;

            font-weight:
                900;
        }

        small {
            color:
                var(--muted);

            font-size:
                11px;

            font-weight:
                750;
        }
    }


    .event-date-pill__day {
        color:
            var(--gold-600);

        font-size:
            38px;

        line-height:
            1;

        font-weight:
            950;

        letter-spacing:
            -0.04em;
    }


    .event-location-pill {
        display:
            flex;

        align-items:
            center;

        gap:
            12px;

        max-width:
            315px;

        padding:
            11px 15px;

        color:
            inherit;

        text-decoration:
            none;

        transition:
            transform 180ms ease,
            border-color 180ms ease,
            box-shadow 180ms ease;

        &:hover {
            transform:
                translateY(-2px);

            border-color:
                rgba(217,
                    154,
                    22,
                    0.34);

            box-shadow:
                var(--shadow-md);
        }

        @media (max-width: 560px) {
            max-width:
                none;

            width:
                100%;
        }
    }


    .event-location-pill__icon {
        display:
            grid;

        width:
            44px;

        height:
            44px;

        place-items:
            center;

        flex:
            0 0 auto;

        border-radius:
            14px;

        color:
            var(--gold-400);

        background:
            linear-gradient(145deg,
                var(--navy-800),
                var(--navy-950));
    }


    .event-location-pill__copy {
        display:
            grid;

        gap:
            1px;

        min-width: 0;

        small {
            color:
                var(--muted);

            font-size:
                9px;

            font-weight:
                900;

            letter-spacing:
                0.11em;

            text-transform:
                uppercase;
        }

        strong {
            overflow:
                hidden;

            color:
                var(--navy-900);

            font-size:
                13px;

            line-height:
                1.3;

            font-weight:
                900;

            text-overflow:
                ellipsis;
        }

        em {
            color:
                var(--gold-600);

            font-size:
                10px;

            font-style:
                normal;

            font-weight:
                800;
        }
    }


    .event-info-grid {
        display:
            grid;

        gap:
            15px;

        @media (min-width: 760px) {
            grid-template-columns:
                repeat(3,
                    minmax(0,
                        1fr));
        }
    }


    .event-info-card {
        display:
            flex;

        align-items:
            center;

        gap:
            15px;

        min-height:
            122px;

        padding:
            20px;

        border:
            1px solid var(--line);

        border-radius:
            var(--radius-lg);

        background:
            #fff;

        box-shadow:
            0 9px 28px rgba(2,
                24,
                43,
                0.055);

        transition:
            transform 180ms ease,
            border-color 180ms ease,
            box-shadow 180ms ease;

        &:hover {
            transform:
                translateY(-4px);

            border-color:
                rgba(217,
                    154,
                    22,
                    0.25);

            box-shadow:
                var(--shadow-md);
        }

        @media (max-width: 560px) {
            min-height:
                auto;
        }
    }


    .event-info-card__icon {
        display:
            grid;

        width:
            54px;

        height:
            54px;

        place-items:
            center;

        flex:
            0 0 auto;

        border-radius:
            17px;

        color:
            var(--gold-400);

        background:
            linear-gradient(145deg,
                var(--navy-800),
                var(--navy-950));
    }


    .event-info-card__copy {
        span {
            display:
                block;

            margin-bottom:
                4px;

            color:
                var(--muted);

            font-size:
                9px;

            font-weight:
                900;

            letter-spacing:
                0.12em;

            text-transform:
                uppercase;
        }

        strong {
            display:
                block;

            color:
                var(--navy-900);

            font-size:
                16px;

            line-height:
                1.35;

            font-weight:
                900;
        }

        small {
            display:
                block;

            margin-top:
                4px;

            color:
                var(--muted);

            font-size:
                11px;

            line-height:
                1.4;

            font-weight:
                700;
        }
    }


    /* =====================================================
       MAP
    ====================================================== */

    .event-map {
        overflow:
            hidden;

        display:
            grid;

        margin-top:
            22px;

        border:
            1px solid var(--line);

        border-radius:
            var(--radius-xl);

        background:
            #fff;

        box-shadow:
            var(--shadow-md);

        @media (min-width: 900px) {
            grid-template-columns:
                320px minmax(0,
                    1fr);

            min-height:
                390px;
        }
    }


    .event-map__copy {
        display:
            flex;

        gap:
            15px;

        align-items:
            flex-start;

        padding:
            28px;

        background:
            linear-gradient(145deg,
                var(--cream-soft),
                #fff);

        h3 {
            margin:
                0 0 8px;

            color:
                var(--navy-900);

            font-size:
                24px;

            line-height:
                1.1;

            font-weight:
                950;
        }

        p:not(.eyebrow) {
            margin:
                0 0 18px;

            color:
                var(--muted);

            line-height:
                1.55;
        }

        @media (min-width: 900px) {
            align-items:
                center;
        }
    }


    .event-map__icon {
        display:
            grid;

        width:
            48px;

        height:
            48px;

        place-items:
            center;

        flex:
            0 0 auto;

        border-radius:
            16px;

        color:
            var(--gold-400);

        background:
            var(--navy-900);
    }


    .map-link {
        display:
            inline-flex;

        align-items:
            center;

        gap:
            7px;

        color:
            var(--gold-600);

        font-size:
            13px;

        font-weight:
            900;

        text-decoration:
            none;

        &:hover {
            text-decoration:
                underline;
        }
    }


    .event-map__frame {
        min-height:
            340px;

        iframe {
            display:
                block;

            width:
                100%;

            height:
                100%;

            min-height:
                340px;

            border: 0;
        }

        @media (min-width: 900px) {
            min-height:
                390px;

            iframe {
                min-height:
                    390px;
            }
        }
    }


    /* =====================================================
       ABOUT + FLYERS
    ====================================================== */

    .bridge-about,
    .audience-section {
        padding:
            84px 0;

        background:
            #fff;

        @media (max-width: 720px) {
            padding:
                56px 0;
        }
    }


    .bridge-about__grid,
    .audience-section__grid {
        display:
            grid;

        gap:
            52px;

        align-items:
            center;

        @media (min-width: 920px) {
            grid-template-columns:
                minmax(0,
                    1fr) 400px;
        }
    }


    .bridge-about__copy,
    .audience-section__copy {
        h2 {
            max-width:
                790px;

            margin:
                0 0 21px;

            color:
                var(--navy-900);

            font-size:
                clamp(36px,
                    4.6vw,
                    60px);

            line-height:
                1;

            font-weight:
                950;

            letter-spacing:
                -0.045em;
        }

        >p {
            max-width:
                65ch;

            margin:
                0 0 16px;

            color:
                var(--muted);

            font-size:
                15px;

            line-height:
                1.8;
        }
    }


    .bridge-about__copy {
        h2 {
            span {
                display:
                    block;

                margin-top:
                    8px;

                color:
                    var(--gold-600);
            }
        }
    }


    .bridge-about__quote {
        display:
            grid;

        grid-template-columns:
            auto minmax(0,
                1fr) auto;

        gap:
            13px;

        align-items:
            start;

        max-width:
            680px;

        margin-top:
            26px;

        padding:
            22px 24px;

        border-radius:
            var(--radius-lg);

        color:
            #fff;

        background:
            radial-gradient(circle at top right,
                rgba(217,
                    154,
                    22,
                    0.2),
                transparent 45%),
            linear-gradient(135deg,
                var(--navy-800),
                var(--navy-950));

        box-shadow:
            var(--shadow-md);

        >span {
            color:
                var(--gold-400);

            font-family:
                Georgia,
                serif;

            font-size:
                38px;

            line-height:
                0.8;
        }

        p {
            margin: 0;

            color:
                rgba(255,
                    255,
                    255,
                    0.87);

            line-height:
                1.65;

            font-weight:
                700;
        }
    }


    .flyer-frame {
        position:
            relative;

        width:
            100%;

        max-width:
            400px;

        margin:
            0 auto;

        padding:
            10px;

        border:
            1px solid rgba(7,
                42,
                73,
                0.09);

        border-radius:
            30px;

        background:
            linear-gradient(145deg,
                #fff,
                #f3f4f4);

        box-shadow:
            var(--shadow-lg);

        &::before {
            position:
                absolute;

            inset:
                6px;

            z-index:
                0;

            content:
                "";

            border:
                1px solid rgba(255,
                    255,
                    255,
                    0.75);

            border-radius:
                24px;

            pointer-events:
                none;
        }

        @media (max-width: 560px) {
            max-width:
                340px;

            padding:
                7px;

            border-radius:
                24px;
        }
    }


    .flyer-button {
        overflow:
            hidden;

        border-radius:
            22px;

        background:
            var(--navy-950);

        img {
            position:
                relative;

            z-index:
                1;

            display:
                block;

            width:
                100%;

            height:
                auto;

            transition:
                transform 350ms ease;
        }

        &:hover {
            img {
                transform:
                    scale(1.015);
            }
        }
    }


    /* =====================================================
       EXPERIENCE
    ====================================================== */

    .experience-section {
        padding:
            84px 0;

        background:
            radial-gradient(620px 300px at 90% 0%,
                rgba(217,
                    154,
                    22,
                    0.09),
                transparent 68%),
            linear-gradient(180deg,
                #f5f7f9,
                #f8fafb);

        @media (max-width: 720px) {
            padding:
                56px 0;
        }
    }


    .experience-grid {
        display:
            grid;

        gap:
            17px;

        @media (min-width: 760px) {
            grid-template-columns:
                repeat(3,
                    minmax(0,
                        1fr));
        }
    }


    .experience-card {
        position:
            relative;

        overflow:
            hidden;

        padding:
            27px;

        border:
            1px solid var(--line);

        border-radius:
            var(--radius-xl);

        background:
            #fff;

        box-shadow:
            var(--shadow-sm);

        transition:
            transform 190ms ease,
            border-color 190ms ease,
            box-shadow 190ms ease;

        &::after {
            position:
                absolute;

            right:
                -90px;

            bottom:
                -120px;

            width:
                230px;

            height:
                230px;

            content:
                "";

            border-radius:
                50%;

            background:
                rgba(217,
                    154,
                    22,
                    0.055);

            pointer-events:
                none;
        }

        &:hover {
            transform:
                translateY(-6px);

            border-color:
                rgba(217,
                    154,
                    22,
                    0.28);

            box-shadow:
                var(--shadow-md);
        }

        h3 {
            position:
                relative;

            z-index:
                2;

            margin:
                19px 0 9px;

            color:
                var(--navy-900);

            font-size:
                23px;

            line-height:
                1.1;

            font-weight:
                950;
        }

        p {
            position:
                relative;

            z-index:
                2;

            margin: 0;

            color:
                var(--muted);

            line-height:
                1.7;
        }
    }


    .experience-card__top {
        position:
            relative;

        z-index:
            2;

        display:
            flex;

        align-items:
            flex-start;

        justify-content:
            space-between;
    }


    .experience-card__icon {
        display:
            grid;

        width:
            59px;

        height:
            59px;

        place-items:
            center;

        border-radius:
            19px;

        color:
            var(--gold-400);

        background:
            var(--navy-900);
    }


    .experience-card__number {
        color:
            rgba(3,
                35,
                62,
                0.1);

        font-size:
            43px;

        line-height:
            1;

        font-weight:
            950;
    }


    /* =====================================================
       AUDIENCE
    ====================================================== */

    .audience-section {
        background:
            #fff;

        .audience-section__grid {
            @media (min-width: 920px) {
                grid-template-columns:
                    400px minmax(0,
                        1fr);
            }
        }
    }


    .audience-list {
        display:
            grid;

        gap:
            9px;

        margin:
            24px 0;
    }


    .audience-list__item {
        display:
            flex;

        align-items:
            center;

        gap:
            11px;

        padding:
            12px 14px;

        border:
            1px solid var(--line);

        border-radius:
            15px;

        background:
            var(--cream-soft);

        transition:
            transform 160ms ease,
            border-color 160ms ease;

        &:hover {
            transform:
                translateX(3px);

            border-color:
                rgba(217,
                    154,
                    22,
                    0.28);
        }

        span {
            display:
                grid;

            width:
                28px;

            height:
                28px;

            place-items:
                center;

            flex:
                0 0 auto;

            border-radius:
                50%;

            color:
                #fff;

            background:
                linear-gradient(145deg,
                    var(--navy-800),
                    var(--gold-500));

            font-size:
                11px;

            font-weight:
                950;
        }

        strong {
            color:
                var(--navy-900);

            font-size:
                13px;

            line-height:
                1.4;

            font-weight:
                850;
        }
    }


    /* =====================================================
       PARTNERS
    ====================================================== */

    .partners-section {
        padding:
            80px 0;

        background:
            radial-gradient(600px 280px at 50% 0%,
                rgba(217,
                    154,
                    22,
                    0.07),
                transparent 70%),
            linear-gradient(180deg,
                var(--cream-soft),
                #fff);

        @media (max-width: 720px) {
            padding:
                54px 0;
        }
    }


    .partners-grid {
        display:
            grid;

        gap:
            15px;

        @media (min-width: 760px) {
            grid-template-columns:
                repeat(3,
                    minmax(0,
                        1fr));
        }
    }


    .partner-card {
        position:
            relative;

        overflow:
            hidden;

        padding:
            25px;

        border:
            1px solid var(--line);

        border-radius:
            var(--radius-lg);

        background:
            #fff;

        box-shadow:
            var(--shadow-sm);

        transition:
            transform 180ms ease,
            border-color 180ms ease,
            box-shadow 180ms ease;

        &::before {
            position:
                absolute;

            top: 0;

            left:
                25px;

            width:
                50px;

            height:
                3px;

            content:
                "";

            border-radius:
                0 0 4px 4px;

            background:
                linear-gradient(90deg,
                    var(--gold-600),
                    var(--gold-300));
        }

        &:hover {
            transform:
                translateY(-5px);

            border-color:
                rgba(217,
                    154,
                    22,
                    0.25);

            box-shadow:
                var(--shadow-md);
        }

        h3 {
            margin:
                11px 0 9px;

            color:
                var(--navy-900);

            font-size:
                23px;

            line-height:
                1.1;

            font-weight:
                950;
        }

        p {
            margin:
                0 0 17px;

            color:
                var(--muted);

            line-height:
                1.65;
        }

        a {
            display:
                inline-flex;

            gap:
                6px;

            color:
                var(--gold-600);

            font-size:
                12px;

            font-weight:
                900;

            text-decoration:
                none;

            &:hover {
                text-decoration:
                    underline;
            }
        }
    }


    .partner-card__type {
        color:
            var(--gold-600);

        font-size:
            9px;

        font-weight:
            900;

        letter-spacing:
            0.13em;

        text-transform:
            uppercase;
    }


    /* =====================================================
       REGISTER
    ====================================================== */

    .register-section {
        padding:
            82px 0;

        background:
            #fff;

        @media (max-width: 720px) {
            padding:
                54px 0;
        }
    }


    .register-panel {
        position:
            relative;

        overflow:
            hidden;

        display:
            grid;

        gap:
            32px;

        align-items:
            center;

        padding:
            clamp(29px,
                5vw,
                54px);

        border-radius:
            38px;

        color:
            #fff;

        background:
            radial-gradient(660px 330px at 100% 0%,
                rgba(217,
                    154,
                    22,
                    0.27),
                transparent 62%),
            linear-gradient(135deg,
                var(--navy-800),
                var(--navy-950));

        box-shadow:
            var(--shadow-lg);

        @media (min-width: 900px) {
            grid-template-columns:
                minmax(0,
                    1fr) auto;
        }

        @media (max-width: 560px) {
            padding:
                27px 20px;

            border-radius:
                28px;
        }
    }


    .register-panel__copy {
        h2 {
            max-width:
                720px;

            margin: 0;

            color:
                #fff;

            font-size:
                clamp(38px,
                    4.8vw,
                    64px);

            line-height:
                0.98;

            font-weight:
                950;

            letter-spacing:
                -0.045em;
        }

        >p {
            max-width:
                670px;

            margin:
                16px 0 0;

            color:
                rgba(255,
                    255,
                    255,
                    0.69);

            line-height:
                1.75;
        }
    }


    .register-panel__details {
        display:
            flex;

        flex-wrap:
            wrap;

        align-items:
            center;

        gap:
            9px;

        margin-top:
            20px;

        color:
            rgba(255,
                255,
                255,
                0.85);

        font-size:
            11px;

        font-weight:
            800;

        a {
            color:
                inherit;

            text-decoration:
                none;

            &:hover {
                color:
                    var(--gold-300);
            }
        }

        i {
            width:
                4px;

            height:
                4px;

            border-radius:
                50%;

            background:
                var(--gold-400);
        }

        @media (max-width: 560px) {
            display:
                grid;

            i {
                display:
                    none;
            }
        }
    }


    .register-panel__action {
        display:
            grid;

        gap:
            9px;

        justify-items:
            center;

        min-width:
            190px;

        small {
            color:
                rgba(255,
                    255,
                    255,
                    0.47);

            font-size:
                10px;

            font-weight:
                700;
        }

        @media (max-width: 560px) {
            width:
                100%;
        }
    }


    /* =====================================================
       LAUNCH
    ====================================================== */

    .launch-strip {
        padding:
            24px 0;

        color:
            #fff;

        background:
            linear-gradient(135deg,
                #021629,
                var(--navy-950));

        border-top:
            1px solid rgba(237,
                179,
                53,
                0.16);
    }


    .launch-strip__inner {
        display:
            flex;

        flex-wrap:
            wrap;

        align-items:
            center;

        justify-content:
            center;

        gap:
            13px;

        text-align:
            center;

        p {
            margin: 0;

            color:
                rgba(255,
                    255,
                    255,
                    0.75);

            font-size:
                11px;

            font-weight:
                700;

            letter-spacing:
                0.08em;

            text-transform:
                uppercase;

            strong {
                color:
                    var(--gold-300);
            }
        }
    }


    .launch-strip__mark {
        color:
            var(--gold-400);

        font-size:
            19px;
    }


    .launch-strip__values {
        display:
            flex;

        align-items:
            center;

        gap:
            9px;

        color:
            rgba(255,
                255,
                255,
                0.65);

        font-size:
            9px;

        font-weight:
            800;

        letter-spacing:
            0.12em;

        text-transform:
            uppercase;

        i {
            width:
                4px;

            height:
                4px;

            border-radius:
                50%;

            background:
                var(--gold-400);
        }
    }


    /* =====================================================
       REDUCED MOTION
    ====================================================== */

    @media (prefers-reduced-motion: reduce) {

        *,
        *::before,
        *::after {
            animation-duration:
                0.001ms !important;

            animation-iteration-count:
                1 !important;

            transition-duration:
                0.001ms !important;

            scroll-behavior:
                auto !important;
        }
    }
}


/* =========================================================
   IMAGE LIGHTBOX

   Teleport renders this outside .bridge-page, therefore
   these must be global selectors.
========================================================= */

:global(.image-lightbox) {
    position:
        fixed;

    inset: 0;

    z-index:
        99999;

    display:
        grid;

    grid-template-rows:
        auto minmax(0, 1fr) auto;

    width:
        100%;

    height:
        100%;

    padding:
        18px;

    color:
        #fff;

    background:
        rgba(1,
            12,
            23,
            0.94);

    backdrop-filter:
        blur(18px);

    overflow:
        hidden;
}


:global(.image-lightbox__topbar) {
    display:
        flex;

    align-items:
        center;

    justify-content:
        space-between;

    gap:
        18px;

    width:
        min(1200px,
            100%);

    margin:
        0 auto;

    padding-bottom:
        12px;
}


:global(.image-lightbox__title) {
    display:
        flex;

    align-items:
        center;

    gap:
        9px;

    min-width: 0;

    color:
        rgba(255,
            255,
            255,
            0.75);

    font-size:
        12px;

    font-weight:
        750;
}


:global(.image-lightbox__title > span:last-child) {
    overflow:
        hidden;

    white-space:
        nowrap;

    text-overflow:
        ellipsis;
}


:global(.image-lightbox__title-icon) {
    display:
        grid;

    width:
        34px;

    height:
        34px;

    place-items:
        center;

    flex:
        0 0 auto;

    border:
        1px solid rgba(255,
            255,
            255,
            0.11);

    border-radius:
        11px;

    color:
        #edb335;

    background:
        rgba(255,
            255,
            255,
            0.07);
}


:global(.image-lightbox__close) {
    appearance:
        none;

    display:
        grid;

    width:
        44px;

    height:
        44px;

    place-items:
        center;

    flex:
        0 0 auto;

    padding: 0;

    border:
        1px solid rgba(255,
            255,
            255,
            0.14);

    border-radius:
        50%;

    color:
        #fff;

    background:
        rgba(255,
            255,
            255,
            0.07);

    cursor:
        pointer;

    transition:
        transform 160ms ease,
        background 160ms ease,
        border-color 160ms ease;
}


:global(.image-lightbox__close span) {
    position:
        relative;

    top:
        -1px;

    font-size:
        28px;

    line-height:
        1;

    font-weight:
        300;
}


:global(.image-lightbox__close:hover) {
    transform:
        scale(1.05);

    border-color:
        rgba(237,
            179,
            53,
            0.42);

    background:
        #b87906;
}


:global(.image-lightbox__viewport) {
    display:
        grid;

    place-items:
        center;

    min-height: 0;

    padding:
        4px 0 8px;

    overflow:
        hidden;
}


:global(.image-lightbox__figure) {
    display:
        grid;

    grid-template-rows:
        minmax(0,
            1fr) auto;

    justify-items:
        center;

    max-width:
        100%;

    max-height:
        100%;

    margin: 0;

    padding:
        8px;

    border:
        1px solid rgba(255,
            255,
            255,
            0.1);

    border-radius:
        22px;

    background:
        rgba(255,
            255,
            255,
            0.045);

    box-shadow:
        0 40px 120px rgba(0,
            0,
            0,
            0.45);
}


/*
 Portrait flyers should never expand to the full
 desktop width. This is the main fix for the
 oversized/awkward flyer view.
*/
:global(.image-lightbox__figure--portrait) {
    width:
        min(620px,
            90vw);
}


/*
 Landscape banner can use more horizontal room.
*/
:global(.image-lightbox__figure--landscape) {
    width:
        min(1180px,
            94vw);
}


:global(.image-lightbox__image) {
    display:
        block;

    width:
        auto;

    max-width:
        100%;

    height:
        auto;

    max-height:
        calc(100vh - 155px);

    object-fit:
        contain;

    border-radius:
        14px;

    user-select:
        none;
}


:global(.image-lightbox__caption) {
    width:
        100%;

    margin:
        8px 0 0;

    padding:
        4px 8px 2px;

    color:
        rgba(255,
            255,
            255,
            0.62);

    font-size:
        11px;

    line-height:
        1.45;

    font-weight:
        650;

    text-align:
        center;
}


:global(.image-lightbox__help) {
    margin:
        8px auto 0;

    color:
        rgba(255,
            255,
            255,
            0.36);

    font-size:
        10px;

    font-weight:
        700;

    text-align:
        center;
}


/* =========================================================
   LIGHTBOX MOBILE
========================================================= */

@media (max-width: 700px) {
    :global(.image-lightbox) {
        padding:
            10px;
    }


    :global(.image-lightbox__topbar) {
        padding:
            2px 2px 9px;
    }


    :global(.image-lightbox__title) {
        font-size:
            10px;
    }


    :global(.image-lightbox__title-icon) {
        width:
            31px;

        height:
            31px;
    }


    :global(.image-lightbox__close) {
        width:
            40px;

        height:
            40px;
    }


    :global(.image-lightbox__figure) {
        padding:
            5px;

        border-radius:
            16px;
    }


    :global(.image-lightbox__figure--portrait),
    :global(.image-lightbox__figure--landscape) {
        width:
            100%;
    }


    :global(.image-lightbox__image) {
        max-height:
            calc(100vh - 130px);

        border-radius:
            11px;
    }


    :global(.image-lightbox__caption) {
        margin-top:
            5px;

        font-size:
            10px;
    }


    :global(.image-lightbox__help) {
        display:
            none;
    }
}


/* =========================================================
   LIGHTBOX TRANSITION
========================================================= */

:global(.lightbox-enter-active),
:global(.lightbox-leave-active) {
    transition:
        opacity 180ms ease;
}


:global(.lightbox-enter-active .image-lightbox__figure) {
    transition:
        transform 220ms ease,
        opacity 220ms ease;
}


:global(.lightbox-enter-from),
:global(.lightbox-leave-to) {
    opacity: 0;
}


:global(.lightbox-enter-from .image-lightbox__figure) {
    opacity: 0;

    transform:
        scale(0.97);
}
</style>